import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Table, Thead, Tbody, Tr, Th, Td, Spinner, Text, Flex, Button, useToast
} from "@chakra-ui/react";
import { TriangleUpIcon, TriangleDownIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import OrdersFilter from "./components/Filters";
import fetchWithToken from "views/auth/signIn/axiosInstance";

export default function PerformanceReport() {
  const { t } = useTranslation();
  const toast = useToast();

  // 🔹 Filters
  const [dateFilter, setDateFilter] = useState("today");
  const [customRange, setCustomRange] = useState({ from: "", to: "" });
  const [branch, setBranch] = useState("all");
  const [filterCompanyId, setFilterCompanyId] = useState("");
  const [selectedService, setSelectedService] = useState("");
  const [ordersPerPage, setOrdersPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [dateSortOrder, setDateSortOrder] = useState("desc");

  // 🔹 Data
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(false);

  const companyId = filterCompanyId;

  // 🔹 Prepare API payload
  const getApiPayload = useCallback(() => {
    const payload = {
      type: "admin",
      page: currentPage,
      limit: ordersPerPage,
      company_id: companyId || undefined,
      branch_id: branch !== "all" ? branch : undefined,
      sort_by: dateSortOrder,
      sort_field: "count",
    };

    if (selectedService) payload.service_id = selectedService;

    if (dateFilter === "today") {
      const d = new Date();
      payload.date_filter = "today";
      payload.from = payload.to = d.toISOString().slice(0, 10);
    } else if (dateFilter === "tomorrow") {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      payload.date_filter = "tomorrow";
      payload.from = payload.to = d.toISOString().slice(0, 10);
    } else if (dateFilter === "after_tomorrow") {
      const d = new Date();
      d.setDate(d.getDate() + 2);
      payload.date_filter = "after_tomorrow";
      payload.from = payload.to = d.toISOString().slice(0, 10);
    } else if (dateFilter === "custom") {
      if (customRange.from) payload.from = customRange.from;
      if (customRange.to) payload.to = customRange.to;
      payload.date_filter = "custom";
    }
    return payload;
  }, [branch, companyId, selectedService, currentPage, customRange, dateFilter, ordersPerPage, dateSortOrder]);

  // 🔹 Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const payload = getApiPayload();
      const res = await fetchWithToken(
        `${process.env.REACT_APP_API_URL_Report}reports/performance`,
        "POST",
        payload
      );
      if (res.status !== "success") throw new Error(res.message || "Failed to fetch report");

      setData(res.data.data || []);
      setTotalPages(res.data.meta.pagination.last_page || 1);
      setTotalResults(res.data.meta.pagination.total || 0);
    } catch (error) {
      toast({
        title: t("Error"),
        description: error.message,
        status: "error",
        position: "top-right",
        duration: 5000,
        isClosable: true,
      });
      setData([]);
      setTotalPages(1);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  }, [getApiPayload, t, toast]);

  // 🔹 Refetch when filters change
  useEffect(() => {
    if (dateFilter === "custom" && (!customRange.from || !customRange.to)) return;
    fetchData();
  }, [fetchData, dateFilter, customRange.from, customRange.to]);

  // 🔹 Sorting
  const handleSortCount = () => {
    setDateSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    setCurrentPage(1);
  };

  // 🔹 Pagination
  const handlePageChange = (page) => setCurrentPage(page);

  return (
    <Box
      p={4}
      bg="white"
      borderRadius="lg"
      boxShadow="0 4px 20px rgba(0,0,0,0.1)"
    >
      
      <OrdersFilter
        dateFilter={dateFilter}
        setDateFilter={setDateFilter}
        branch={branch}
        setBranch={setBranch}
        companyId={companyId}
        setCompanyId={setFilterCompanyId}
        customRange={customRange}
        setCustomRange={setCustomRange}
        ordersPerPage={ordersPerPage}
        setOrdersPerPage={setOrdersPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        showStatusFilter={false}
        selectedService={selectedService}
        setSelectedService={setSelectedService}
      />

      <Box mt={6}>
        {loading ? (
          <Flex justify="center" align="center" minH="200px">
            <Spinner size="xl" />
          </Flex>
        ) : data.length === 0 ? (
          <Text textAlign="center" color="gray.500">{t("No data found")}</Text>
        ) : (
          <Table variant="striped" colorScheme="gray">
            <Thead>
              <Tr bg="#3c31ce">
                <Th color="white">{t("Company Name")}</Th>
                <Th color="white">{t("Branch Name")}</Th>
                <Th color="white">{t("Service Name")}</Th>
                <Th color="white">{t("Item Name")}</Th>
                <Th color="white" cursor="pointer" onClick={handleSortCount}>
                  {t("Count")}
                  {dateSortOrder === "asc" ? (
                    <TriangleUpIcon ml={2} />
                  ) : (
                    <TriangleDownIcon ml={2} />
                  )}
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.map((row, idx) => (
                <Tr key={idx}>
                  <Td>{row.company_name}</Td>
                  <Td>{row.branch_name}</Td>
                  <Td>{row.service_name}</Td>
                  <Td>{row.item_name}</Td>
                  <Td>{row.count}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      {/* Pagination and Info */}
      {totalPages > 1 && (
        <Flex justify="center" align="center" mt={6} gap={2} wrap="wrap">
          <Button onClick={() => handlePageChange(1)} disabled={currentPage === 1} size="sm" variant="outline">
            {t("First")}
          </Button>
          <Button onClick={() => handlePageChange(Math.max(currentPage - 1, 1))} disabled={currentPage === 1} size="sm" variant="outline">
            {t("Previous")}
          </Button>

          {(() => {
            const delta = 2;
            const range = [];
            const rangeWithDots = [];
            for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
              range.push(i);
            }
            if (currentPage - delta > 2) rangeWithDots.push(1, "...");
            else rangeWithDots.push(1);
            rangeWithDots.push(...range);
            if (currentPage + delta < totalPages - 1) rangeWithDots.push("...", totalPages);
            else if (totalPages > 1) rangeWithDots.push(totalPages);

            return rangeWithDots.map((page, index) =>
              page === "..." ? (
                <Text key={`ellipsis-${index}`} mx={2} alignSelf="center" fontSize="sm" color="gray.500">...</Text>
              ) : (
                <Button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  variant={currentPage === page ? "solid" : "outline"}
                  colorScheme={currentPage === page ? "cyan" : "gray"}
                  size="sm"
                  minW="40px"
                  isDisabled={currentPage === page}
                >
                  {page}
                </Button>
              )
            );
          })()}

          <Button onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))} disabled={currentPage === totalPages} size="sm" variant="outline">
            {t("Next")}
          </Button>
          <Button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} size="sm" variant="outline">
            {t("Last")}
          </Button>
        </Flex>
      )}

      <Text mt={4} textAlign="center" fontSize="sm" color="gray.600">
        {t("Page")} {currentPage} {t("of")} {totalPages} - {t("Showing")} {(data.length > 0) ? ((currentPage - 1) * ordersPerPage + 1) : 0} {t("to")} {((currentPage - 1) * ordersPerPage) + data.length} {t("results")}
      </Text>
    </Box>
  );
}
